import React, { useState, useEffect, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { ActivoFichaPrint } from './ActivoFichaPrint';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_URLS, BASE_URL } from '../../apiConfig';

// Parsea accesorios desde JSON o CSV (compatibilidad con datos anteriores)
// Siempre devuelve objetos con: { nombre, cantidad, cantidad_total }
const parseAccesorios = (raw) => {
    if (!raw || raw.trim() === '') return [];
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            return parsed.map(a => ({
                nombre: a.nombre,
                cantidad: Math.max(0, a.cantidad ?? 0),
                cantidad_total: a.cantidad_total ?? a.cantidad ?? 0
            }));
        }
    } catch {}
    // Formato CSV anterior
    return raw.split(',').map(a => a.trim()).filter(a => a)
              .map(nombre => ({ nombre, cantidad: 1, cantidad_total: 1 }));
};

function ActivosManagement() {
    const contentRef = useRef(null);

    const handlePrint = useReactToPrint({
        contentRef: contentRef,
    });

    const initialFormData = {
        id: null, codigo: '', nombre: '', descripcion: '', marca: '',
        serie: '', accesorios: '', tipo_equipo: '', capacidad: '',
        ubicacion: '', tipo_energia: '', empresa: '', precio_compra: 0,
        cantidad: 0, foto: null
    };

    const [formData, setFormData] = useState(initialFormData);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [destinos, setDestinos] = useState([]);
    // Cantidad actual en depósito (solo lectura en edición, no se edita directamente)
    const [stockActual, setStockActual] = useState(null);

    // Cada accesorio: { nombre: string, cantidad: number }
    const [listaAccesorios, setListaAccesorios] = useState([]);
    const [nuevoAccesorio, setNuevoAccesorio] = useState({ nombre: '', cantidad: 1 });

    useEffect(() => {
        const fetchDestinos = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${BASE_URL}/destinos`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setDestinos(response.data);
            } catch (error) {
                console.error("Error al cargar destinos", error);
            }
        };
        fetchDestinos();
    }, []);

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setFormData(prev => ({ ...prev, foto: e.target.files[0] }));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const addAccesorio = () => {
        const nombre = nuevoAccesorio.nombre.trim();
        const cantidad = Math.max(1, parseInt(nuevoAccesorio.cantidad) || 1);
        if (nombre) {
            // cantidad_total se fija al momento de crear el accesorio
            setListaAccesorios(prev => [...prev, { nombre, cantidad, cantidad_total: cantidad }]);
            setNuevoAccesorio({ nombre: '', cantidad: 1 });
        }
    };

    const removeAccesorio = (index) => {
        setListaAccesorios(prev => prev.filter((_, i) => i !== index));
    };

    const updateCantidadAccesorio = (index, valor) => {
        const cantidad = Math.max(0, parseInt(valor) || 0);
        // Al editar desde el formulario maestro, se actualiza también el total
        setListaAccesorios(prev => prev.map((a, i) =>
            i === index ? { ...a, cantidad, cantidad_total: cantidad } : a
        ));
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!searchTerm) return;
        setLoading(true);
        setMessage('');
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URLS.ACTIVOS}/codigo/${searchTerm}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = response.data;
            // Normalizar campos numéricos que pueden venir vacíos de la DB
            // En edición, el campo "Cantidad" muestra cantidad_total (el total de referencia)
            // El stock actual (cantidad) se muestra aparte como info de solo lectura
            setStockActual(data.cantidad ?? 0);
            setFormData({
                ...data,
                precio_compra: data.precio_compra != null && data.precio_compra !== '' ? parseFloat(data.precio_compra) : 0,
                cantidad: data.cantidad_total ?? data.cantidad ?? 0
            });
            setListaAccesorios(parseAccesorios(data.accesorios));
            setIsEditMode(true);
            setIsError(false);
            setMessage("Activo encontrado.");
        } catch (error) {
            setIsEditMode(false);
            setFormData({ ...initialFormData, codigo: searchTerm });
            setListaAccesorios([]);
            setMessage("No encontrado. Puede registrarlo.");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (parseInt(formData.cantidad) < 0) {
            setIsError(true);
            setMessage("La cantidad no puede ser un valor negativo.");
            return;
        }

        setLoading(true);
        const data = new FormData();

        const accesoriosJSON = JSON.stringify(listaAccesorios);

        Object.keys(formData).forEach(key => {
            if (key === 'accesorios') {
                data.append('accesorios', accesoriosJSON);
            } else if (key === 'precio_compra') {
                const precio = parseFloat(formData[key]);
                data.append(key, isNaN(precio) || precio < 0 ? 0 : precio);
            } else if (key === 'cantidad') {
                data.append(key, parseInt(formData[key]) || 0);
            } else if (key !== 'foto' && formData[key] !== null && formData[key] !== undefined) {
                data.append(key, formData[key]);
            }
        });

        if (formData.foto instanceof File) {
            data.append('foto', formData.foto);
        }

        try {
            const token = localStorage.getItem('token');
            const url = isEditMode ? `${API_URLS.ACTIVOS}/${formData.id}` : API_URLS.ACTIVOS;

            await axios({
                method: isEditMode ? 'put' : 'post',
                url: url,
                data: data,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            setMessage(isEditMode ? "¡Actualizado con éxito!" : "¡Registrado con éxito!");
            setIsError(false);
            if (!isEditMode) handleReset();
        } catch (error) {
            setIsError(true);
            // Mostrar el mensaje específico del backend (ej: código duplicado)
            setMessage(error.response?.data?.error || "Error al procesar la solicitud.");
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setFormData(initialFormData);
        setSearchTerm('');
        setIsEditMode(false);
        setMessage('');
        setListaAccesorios([]);
        setNuevoAccesorio({ nombre: '', cantidad: 1 });
        setStockActual(null);
    };

    return (
        <div className="container mt-4 mb-5">
            <div className="card shadow-sm border-0">
                <div className="card-header bg-dark text-white py-3">
                    <h4 className="mb-0 d-flex align-items-center">
                        <i className="bi bi-tools me-2"></i> Gestión de Activos y Maquinaria
                    </h4>
                </div>

                <div className="card-body p-4">
                    {/* BUSCADOR */}
                    <div className="bg-light p-3 rounded mb-4 border">
                        <form onSubmit={handleSearch} className="row g-2 align-items-end">
                            <div className="col-md-4">
                                <label className="form-label small fw-bold text-muted">Búsqueda Rápida</label>
                                <div className="input-group">
                                    <span className="input-group-text"><i className="bi bi-search"></i></span>
                                    <input type="text" className="form-control" placeholder="Código..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                                </div>
                            </div>
                            <div className="col-md-2">
                                <button type="submit" className="btn btn-primary w-100" disabled={loading}>Verificar</button>
                            </div>
                        </form>
                    </div>

                    {message && (
                        <div className={`alert ${isError ? 'alert-danger' : 'alert-success'} alert-dismissible fade show`}>
                            {message}
                            <button type="button" className="btn-close" onClick={() => setMessage('')}></button>
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        {/* IDENTIFICACIÓN */}
                        <div className="row g-3 mb-4">
                            <div className="col-md-3">
                                <label className="form-label fw-bold">Código Interno</label>
                                <input type="text" className="form-control border-primary fw-bold" name="codigo" value={formData.codigo} onChange={handleChange} required />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-bold">Nombre del Activo</label>
                                <input type="text" className="form-control" name="nombre" value={formData.nombre} onChange={handleChange} required />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label fw-bold">
                                    Cantidad Total
                                    {isEditMode && <span className="text-muted fw-normal ms-1" style={{fontSize:'0.78rem'}}>(referencia)</span>}
                                </label>
                                <input type="number" className="form-control" name="cantidad" min="0" value={formData.cantidad} onChange={handleChange} />
                                {isEditMode && stockActual !== null && (
                                    <div className="mt-1 px-2 py-1 rounded" style={{background:'#e8f4fd', fontSize:'0.8rem', color:'#0d6efd'}}>
                                        <i className="bi bi-box-seam me-1"></i>
                                        <strong>En depósito ahora: {stockActual} u.</strong>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ESPECIFICACIONES */}
                        <div className="row g-3 mb-4 p-3 rounded border" style={{backgroundColor: '#f8f9fa'}}>
                            <div className="col-md-3">
                                <label className="form-label small fw-bold">Marca</label>
                                <input type="text" className="form-control" name="marca" value={formData.marca} onChange={handleChange} />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label small fw-bold">N° Serie</label>
                                <input type="text" className="form-control" name="serie" value={formData.serie} onChange={handleChange} />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label small fw-bold">Ubicación</label>
                                <select className="form-select" name="ubicacion" value={formData.ubicacion} onChange={handleChange}>
                                    <option value="">Seleccionar...</option>
                                    {destinos.map(d => <option key={d.id} value={d.nombre}>{d.nombre}</option>)}
                                </select>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label small fw-bold">Energía</label>
                                <select className="form-select" name="tipo_energia" value={formData.tipo_energia} onChange={handleChange}>
                                    <option value="">Seleccione...</option>
                                    <option value="Naftero">Naftero</option>
                                    <option value="Diesel">Diesel</option>
                                    <option value="Batería">Batería</option>
                                    <option value="220V">220V</option>
                                </select>
                            </div>
                        </div>

                        {/* SECCIÓN ACCESORIOS CON CANTIDADES */}
                        <div className="row g-3 mb-4">
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Precio Compra</label>
                                <input type="number" step="0.01" min="0" className="form-control mb-3" name="precio_compra" value={formData.precio_compra ?? 0} onChange={(e) => { const v = parseFloat(e.target.value); setFormData(prev => ({ ...prev, precio_compra: isNaN(v) || v < 0 ? 0 : v })); }} />
                                <label className="form-label fw-bold">Cargar Foto</label>
                                <input type="file" className="form-control" onChange={handleFileChange} />
                            </div>
                            <div className="col-md-8">
                                <label className="form-label fw-bold">Accesorios con Cantidad</label>
                                <div className="input-group mb-2">
                                    <input
                                        type="text"
                                        className="form-control border-primary"
                                        placeholder="Nombre del accesorio..."
                                        value={nuevoAccesorio.nombre}
                                        onChange={(e) => setNuevoAccesorio(prev => ({ ...prev, nombre: e.target.value }))}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAccesorio(); } }}
                                    />
                                    <input
                                        type="number"
                                        className="form-control border-primary"
                                        style={{ maxWidth: '90px' }}
                                        placeholder="Cant."
                                        min="1"
                                        value={nuevoAccesorio.cantidad}
                                        onChange={(e) => setNuevoAccesorio(prev => ({ ...prev, cantidad: e.target.value }))}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAccesorio(); } }}
                                    />
                                    <button
                                        className="btn btn-primary px-4 fw-bold shadow-sm"
                                        type="button"
                                        onClick={addAccesorio}
                                    >
                                        <i className="bi bi-plus-lg me-1"></i> AGREGAR
                                    </button>
                                </div>

                                {/* Lista de accesorios con cantidad editable */}
                                <div className="p-3 border rounded bg-white shadow-sm" style={{ minHeight: '60px' }}>
                                    {listaAccesorios.length === 0 && <small className="text-muted">Sin accesorios.</small>}
                                    <div className="d-flex flex-wrap gap-2">
                                        {listaAccesorios.map((acc, index) => (
                                            <div key={index} className="d-flex align-items-center bg-dark text-white rounded px-2 py-1 gap-2" style={{ fontSize: '0.85rem' }}>
                                                <span className="fw-bold">{acc.nombre}</span>
                                                <span className="badge bg-warning text-dark mx-1" style={{ fontSize: '0.75rem' }}>x</span>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={acc.cantidad}
                                                    onChange={(e) => updateCantidadAccesorio(index, e.target.value)}
                                                    className="form-control form-control-sm bg-secondary border-0 text-white text-center fw-bold p-0"
                                                    style={{ width: '52px', height: '24px', fontSize: '0.8rem' }}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn-close btn-close-white ms-1"
                                                    style={{ fontSize: '0.55rem' }}
                                                    onClick={() => removeAccesorio(index)}
                                                    aria-label="Eliminar"
                                                ></button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="mt-3">
                                    <label className="form-label fw-bold small">Descripción Adicional</label>
                                    <textarea className="form-control" name="descripcion" rows="2" value={formData.descripcion} onChange={handleChange}></textarea>
                                </div>
                            </div>
                        </div>

                        <div className="text-end pt-3 border-top d-flex justify-content-end align-items-center">
                            <button type="button" className="btn btn-outline-secondary me-2" onClick={handleReset}>Limpiar</button>

                            {isEditMode && (
                                <button
                                    type="button"
                                    className="btn btn-dark me-2 d-flex align-items-center shadow-sm"
                                    onClick={handlePrint}
                                >
                                    <i className="bi bi-printer me-2"></i> IMPRIMIR FICHA
                                </button>
                            )}

                            <button type="submit" className={`btn ${isEditMode ? 'btn-warning text-dark fw-bold' : 'btn-success'} px-5 shadow`} disabled={loading}>
                                {isEditMode ? 'ACTUALIZAR DATOS' : 'REGISTRAR ACTIVO'}
                            </button>
                        </div>
                    </form>

                    {/* COMPONENTE OCULTO PARA IMPRESIÓN */}
                    <div style={{ display: 'none' }}>
                        <ActivoFichaPrint
                            ref={contentRef}
                            data={formData}
                            accesorios={listaAccesorios}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ActivosManagement;
